import { WORLD_MAP_MAX_ZOOM, WORLD_MAP_MIN_ZOOM, WORLD_MAP_WHEEL_MAX_DELTA, WORLD_MAP_WHEEL_ZOOM_SENSITIVITY } from "@components/world-map/world-map-constants";
import { useEffect } from "react";

/**
 * Handles trackpad pinch-zoom for the 3D scene.
 *
 * The regular mouse-wheel / two-finger-scroll zoom is left to ArcGIS's native
 * navigation, which zooms toward the pointer reliably (the same behavior as the
 * 2D map). We only intercept trackpad *pinch* gestures: those arrive as `ctrlKey`
 * wheel events that ArcGIS ignores, so without this the browser would zoom the
 * whole page. For pinch we apply a direct, immediate zoom (no animation loop) so
 * it stays snappy and never drifts. The 2D map keeps native behavior, so this
 * hook is a no-op there.
 *
 * @param viewRef - Ref to the ArcGIS view (only the 3D SceneView is handled).
 * @param isViewReady - Whether the view has finished initializing.
 */
export function useMapWheelZoom(viewRef, isViewReady) {
  useEffect(() => {
    const view = viewRef.current;
    const container = view?.container;
    if (!isViewReady || !view || !container || view.type !== "3d") {
      return;
    }
    function handlePinch(event) {
      // Only trackpad pinches (ctrlKey wheels), which ArcGIS ignores. Regular
      // wheel / two-finger-scroll events fall through to ArcGIS's native
      // cursor-anchored zoom (the same reliable behavior as the 2D map).
      if (!event.ctrlKey || !view) {
        return;
      }
      event.preventDefault();

      // Clamp the per-event delta so one gesture step is small and predictable,
      // then apply it directly. Center-anchored and immediate: reliable, with no
      // easing tail or feedback loop.
      const clampedDelta = Math.sign(event.deltaY) * Math.min(Math.abs(event.deltaY), WORLD_MAP_WHEEL_MAX_DELTA);
      view.zoom = Math.min(Math.max(view.zoom - clampedDelta * WORLD_MAP_WHEEL_ZOOM_SENSITIVITY, WORLD_MAP_MIN_ZOOM), WORLD_MAP_MAX_ZOOM);
    }

    // `passive: false` is required so `preventDefault()` can block the page zoom.
    container.addEventListener("wheel", handlePinch, {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Name defined by the DOM API
      passive: false
    });
    return () => {
      container.removeEventListener("wheel", handlePinch);
    };
  }, [viewRef, isViewReady]);
}